import { useState, useMemo, useCallback } from "react";
import { Calendar, dateFnsLocalizer, type View } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { th } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "../lib/router";
import leaveService, { type LeaveRequest } from "../services/leave.service";
import { MainLayout } from "../components/layout";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../styles/calendar.css";

// ตั้งค่า localizer สำหรับภาษาไทย
const locales = {
  th: th,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: th }),
  getDay,
  locales,
});

// ปรับแต่ง messages เป็นภาษาไทย
const messages = {
  allDay: "ทั้งวัน",
  previous: "ก่อนหน้า",
  next: "ถัดไป",
  today: "วันนี้",
  month: "เดือน",
  week: "สัปดาห์",
  day: "วัน",
  agenda: "รายการ",
  date: "วันที่",
  time: "เวลา",
  event: "กิจกรรม",
  noEventsInRange: "ไม่มีการลาในช่วงเวลานี้",
  showMore: (total: number) => `+${total} เพิ่มเติม`,
};

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: LeaveRequest;
  allDay: boolean;
}

export default function LeaveCalendar() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<View>("month");
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );

  // Check if user is admin or HR
  const isAdminOrHR =
    currentUser?.role === "admin" || currentUser?.role === "hr";

  // Fetch leave requests
  const { data: leaveData, isLoading } = useQuery({
    queryKey: [
      "leaveRequests",
      "calendar",
      date.getMonth(),
      date.getFullYear(),
    ],
    queryFn: () => {
      const params: any = {};

      // Admin/HR ดูการลาของทุกคน
      if (isAdminOrHR) {
        params.allUsers = "true";
      }

      return leaveService.getLeaveRequests(params);
    },
    staleTime: 30 * 1000,
  });

  // แปลง leave requests เป็น calendar events
  const events: CalendarEvent[] = useMemo(() => {
    if (!leaveData?.requests) return [];

    return leaveData.requests.map((request) => {
      const startDate = new Date(request.startDate);
      const endDate = new Date(request.endDate);

      // ปรับเวลาให้เป็น local time
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const userName = request.user
        ? `${request.user.firstName} ${request.user.lastName}`
        : "ไม่ทราบชื่อ";

      return {
        id: request.id,
        title: isAdminOrHR
          ? `${userName} - ${request.leaveType?.name || "ลา"}`
          : request.leaveType?.name || "ลา",
        start: startDate,
        end: endDate,
        resource: request,
        allDay: true,
      };
    });
  }, [leaveData, isAdminOrHR]);

  // สีสำหรับแต่ละสถานะ
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const status = event.resource.status;
    let backgroundColor = "#3182ce"; // default blue
    let borderColor = "#2c5282";

    switch (status) {
      case "approved":
        backgroundColor = "#48bb78"; // green
        borderColor = "#38a169";
        break;
      case "pending":
        backgroundColor = "#ecc94b"; // yellow
        borderColor = "#d69e2e";
        break;
      case "rejected":
        backgroundColor = "#f56565"; // red
        borderColor = "#e53e3e";
        break;
      case "cancelled":
        backgroundColor = "#a0aec0"; // gray
        borderColor = "#718096";
        break;
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        borderWidth: "2px",
        borderStyle: "solid",
        borderRadius: "6px",
        color: "white",
        padding: "4px 8px",
        fontSize: "0.875rem",
        fontWeight: "500",
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      },
    };
  }, []);

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  const handleNavigate = (newDate: Date) => {
    setDate(newDate);
  };

  const handleViewChange = (newView: View) => {
    setView(newView);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "รอพิจารณา";
      case "approved":
        return "อนุมัติ";
      case "rejected":
        return "ปฏิเสธ";
      case "cancelled":
        return "ยกเลิก";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  return (
    <MainLayout title="ปฏิทินการลา">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                ปฏิทินการลา
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {isAdminOrHR
                  ? "ดูการลาของพนักงานทั้งหมด"
                  : "ดูการลาของคุณในรูปแบบปฏิทิน"}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate("/leave/request")}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                + สร้างคำขอใหม่
              </button>
              <button
                onClick={() => navigate("/leaves")}
                className="bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 transition-colors"
              >
                📋 รายการ
              </button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">สถานะ:</span>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: "#48bb78" }}
                ></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">อนุมัติ</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: "#ecc94b" }}
                ></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">รอพิจารณา</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: "#f56565" }}
                ></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">ปฏิเสธ</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: "#a0aec0" }}
                ></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">ยกเลิก</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="p-6">
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 700 }}
                view={view}
                onView={handleViewChange}
                date={date}
                onNavigate={handleNavigate}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                messages={messages}
                culture="th"
                popup
                selectable
                toolbar
                formats={{
                  monthHeaderFormat: (date) =>
                    format(date, "MMMM yyyy", { locale: th }),
                  dayHeaderFormat: (date) =>
                    format(date, "EEEE", { locale: th }),
                  dayRangeHeaderFormat: ({ start, end }) =>
                    `${format(start, "d MMM", { locale: th })} - ${format(end, "d MMM yyyy", { locale: th })}`,
                  agendaHeaderFormat: ({ start, end }) =>
                    `${format(start, "d MMM", { locale: th })} - ${format(end, "d MMM yyyy", { locale: th })}`,
                }}
              />
            </div>
          )}
        </div>

        {/* Event Detail Modal */}
        {selectedEvent && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  รายละเอียดการลา
                </h3>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    ประเภทการลา
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100 mt-1">
                    {selectedEvent.resource.leaveType?.icon}{" "}
                    {selectedEvent.resource.leaveType?.name}
                  </p>
                </div>

                {isAdminOrHR && selectedEvent.resource.user && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      ผู้ขอลา
                    </label>
                    <p className="text-lg text-gray-900 dark:text-gray-100 mt-1">
                      {selectedEvent.resource.user.firstName}{" "}
                      {selectedEvent.resource.user.lastName}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedEvent.resource.user.email}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    ช่วงเวลา
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100 mt-1">
                    {format(selectedEvent.start, "d MMMM yyyy", { locale: th })}
                    {" ถึง "}
                    {format(selectedEvent.end, "d MMMM yyyy", { locale: th })}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    ({selectedEvent.resource.totalDays} วัน)
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    สถานะ
                  </label>
                  <p className="mt-1">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedEvent.resource.status)}`}
                    >
                      {getStatusText(selectedEvent.resource.status)}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    เหตุผล
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 mt-1">
                    {selectedEvent.resource.reason}
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => {
                      navigate(`/leaves/${selectedEvent.id}`);
                    }}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    ดูรายละเอียดเพิ่มเติม
                  </button>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    ปิด
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
